import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Button, Skeleton, Stack, Typography, useTheme, Tooltip } from '@mui/material'
import { FormProvider, useForm, Controller, useFormContext } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import moment from 'moment'

import FormTextInput from 'packages/ui/TextField'
import Switch from 'packages/ui/Switch'
import { DatePicker, DatePickerAnchorField } from 'components/global/CUK/DatePicker'
import { toast } from 'components/global/CUK/Toast/Toast'

import { useSelection } from '../store'
import { useDetail } from '../use-bypass'
import useSave, {
  buildUnitSchema,
  buildImportSchema,
  buildActivateSchema,
  useImport,
  useActivate,
  useActivateMulti
} from '../use-actions'
import Modal, { ModalActivate } from './Modal'
import FilePicker from './FilePicker'


const ActionButtons = ({
  onConfirm,
  loading = false,
  submitLabel = 'ui_elements.create'
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  return (
    <Stack gap={2} direction={'row'} alignSelf={'end'} mt={4}>
      <Button size={'small'} variant={'outlined'} onClick={() => navigate('..')} disabled={loading}>
        {t('ui_elements.cancel')}
      </Button>
      <Button size={'small'} variant={'contained'} onClick={onConfirm} disabled={loading}>
        {loading ? t('ui_elements.in_progress') : t(submitLabel)}
      </Button>
    </Stack>
  )
}

const DeactivatedAt = () => {
  const { control, watch, setValue } = useFormContext()
  const { t } = useTranslation()
  const permanently = watch('permanently')
  return (
    <>
      <Stack>
        <Typography component={'span'} sx={{ fontSize: 12, color: 'content.secondary' }}>{t('bypass.form.permanently')}</Typography>
        <Switch
          onClick={() => {
            setValue('deactivated_at', !permanently ? null : new Date(), { shouldValidate: true })
            setValue('permanently', !permanently, { shouldValidate: true })
          }}
          checked={permanently}
        />
      </Stack>
      {
        !permanently ? (
          <Tooltip title={t('bypass.validity_date_tooltip')} placement={'top-start'}>
            <Stack direction={'row'} gap={.5} sx={{color: 'content.primary'}} alignItems={'center'}>
              <Typography component={'span'} sx={{ fontSize: 12, color: 'content.secondary' }}>{t('bypass.validity_date')}</Typography>
              <i className={'fa-regular fa-circle-info'} style={{fontSize: 12}} />
            </Stack>
          </Tooltip>
        ) : <Box height={18} />
      }
      <Controller
        control={control}
        name="deactivated_at"
        render={({ field: { value, onChange } }) => {
          if (permanently) {
            return <Box sx={{ height: 32 }} />
          }
          return (
            <DatePicker
              placement='left'
              name="deactivated_at"
              defaultStartDate={value}
              field={
                <DatePickerAnchorField
                  isdate={true}
                  locale="fr"
                  startDate={value}
                  endDate={null}
                  placeholder={t('coupons.create.dates_interval_placeholder')}
                  onReset={() => { }}
                />
              }
              onSubmit={onChange}
              singleDateMode={true}
            />
          )
        }}
      />
    </>
  )
}
const FormCreate = ({ item }) => {
  const { t } = useTranslation()
  const [save, { loading, error }] = useSave()
  const navigate = useNavigate()
  const methods = useForm({
    defaultValues: {
      ...item,
      deactivated_at: item?.deactivated_at ? moment(item.deactivated_at).toDate() : null,
      permanently: !item?.deactivated_at
    },
    resolver: yupResolver(buildUnitSchema({ t }))
  })
  const handleSubmit = async (values) => {
    const { error } = await save(values)
    if (error) {
      toast.error(t('bypass.save.error'))
    } else {
      toast.success(t('bypass.save.success'))
      navigate(item ? '../..' : '..')
    }
  }

  return (
    <FormProvider {...methods}>
      {
        Boolean(error) && (
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'error.main' }}>
            {t(`api_errors.${error}`)}
          </Typography>
        )
      }
      <FormTextInput
        name="mac_address"
        label={t('bypass.form.mac_address')}
        placeholder={t('bypass.form.mac_address_placeholder')}
        disabled={Boolean(item)}
        required
      />
      <FormTextInput
        name="description"
        label={t('bypass.form.description')}
        placeholder={t('bypass.form.mac_description_tooltip')}
        multiline
      />
      <DeactivatedAt />

      <ActionButtons
        loading={loading}
        submitLabel={item ? 'ui_elements.update' : 'ui_elements.create'}
        onConfirm={methods.handleSubmit(handleSubmit)}
      />
    </FormProvider>
  )
}

const FormImport = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const [launch, { loading, error }] = useImport()
  const methods = useForm({
    defaultValues: {
      deactivated_at: null,
      lines: [],
      permanently: true,
    },
    resolver: yupResolver(buildImportSchema({ t }))
  })

  const handleSubmit = async ({ deactivated_at, lines }) => {
    const { error } = await launch({ deactivated_at, lines })
    if (error) {
      toast.error(t('bypass.import.toast.error'))
    } else {
      navigate('..')
      toast.success(t('bypass.import.toast.success'))
    }
  }

  return (
    <FormProvider {...methods}>
      <Box sx={{ fontSize: 12, color: 'content.secondary' }} dangerouslySetInnerHTML={{ __html: t('bypass.form.import_description') }} />
      <a
        href={'/docs/bypass.csv'}
        target={'_blank'}
        style={{
          color: theme.palette.primary.main,
          fontSize: 12,
        }}
      >{t('bypass.form.import_template')}</a>
      {
        Boolean(error) && (
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'error.main' }}>
            {t(`errors.bypass.${error}`)}
          </Typography>
        )
      }
      <FilePicker
        onFileChange={(content) => {
          methods.setValue('lines', content.map(({ mac_address, description }) => ({ mac_address, description })), { shouldValidate: true })
        }}
      />
      {
        methods.formState.errors.lines && (
          <Typography sx={{ color: 'error.main', fontSize: 12 }}>{methods.formState.errors.lines.message}</Typography>
        )
      }
      <DeactivatedAt />
      <Box sx={{ height: 50 }} />
      <ActionButtons onConfirm={methods.handleSubmit(handleSubmit)} loading={loading} />
    </FormProvider>
  )
}

export const FormActivate = ({ onClose, onSuccess }) => {
  const [error, setError] = useState<string | null>(null)
  const { t } = useTranslation()
  const [selection] = useSelection()
  const { id } = useParams()
  const [activate, { loading }] = useActivate()
  const [activateMulti, { loading: loadingMulti }] = useActivateMulti()
  const methods = useForm({
    defaultValues: {
      deactivated_at: new Date(),
      permanently: false,
    },
    resolver: yupResolver(buildActivateSchema({ t }))
  })
  const handleSubmit = async ({ deactivated_at }) => {
    let result, resultMulti
    if (selection.length) {
      resultMulti = await activateMulti(selection.map(({ id }) => id), deactivated_at)
    } else {
      result = await activate(id as string, deactivated_at)
    }
    const error = result?.error || resultMulti?.error
    if (error) {
      setError(error as string)
    } else {
      toast.success(t('bypass.activate.success', { count: selection.length }))
      onSuccess()
    }
  }

  return (
    <ModalActivate onClose={onClose}>
      <FormProvider {...methods}>
        {
          Boolean(error) && (
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'error.main' }}>
              {error}
            </Typography>
          )

        }
        <DeactivatedAt />
        <Box sx={{ height: 50 }} />
        <ActionButtons onConfirm={methods.handleSubmit(handleSubmit)} loading={loading || loadingMulti} submitLabel={'bypass.actions.activate'} />
      </FormProvider>
    </ModalActivate>
  )
}


const FormGlobal = ({ hasDisclaimer = true }) => {
  const [typeForm, setTypeForm] = useState('by_unit')
  const { loading, item } = useDetail()
  return (
    <Modal onShowForm={(value) => setTypeForm(value)} item={item} hasDisclaimer={hasDisclaimer}>
      {loading && <Skeleton width={'100%'} height={424} />}
      {typeForm === 'by_unit' ? <FormCreate item={item} /> : <FormImport />}
    </Modal>
  )
}

export default FormGlobal